/**
 * Hearts bot.
 *
 * - Passing: hands over the most dangerous cards (Q♠, high spades without cover, high hearts, high cards in short suits).
 * - Play: duck under the current winner whenever possible (shedding the highest card that still loses), lead low from safe
 *   suits, never lead into a spade the bot could be forced to cover with the Q♠, and when void dump the Q♠, then dangerous
 *   spades, then the highest heart, then the highest card. NORMAL and above track which cards are still unseen.
 * The bot only ever reads its own seat's view.
 */
import { buildDeck, parseCard, type CardId } from '../../core/cards.js';
import { chance, type BotContext, type BotEngine } from '../../bots/types.js';
import { heartsCardPoints, heartsChoosePass, QUEEN_SPADES, type HeartsAction, type HeartsLegal, type HeartsTrickPlay, type HeartsView } from './engine.js';

function unseenCards(view: HeartsView, ctx: BotContext): Set<CardId> {
  const seen = new Set<CardId>(view.hand);
  view.trick.forEach((p) => seen.add(p.card));
  const n = view.tricks.length;
  view.tricks.forEach((t, i) => {
    // Forgetful bots only recall the most recent tricks.
    if (ctx.profile.memory >= 1 || i >= n - Math.max(1, Math.round(n * ctx.profile.memory))) for (const p of t.plays) seen.add(p.card);
  });
  return new Set(buildDeck().filter((c) => !seen.has(c)));
}

const rank = (c: CardId) => parseCard(c).rank;
const lowestOf = (cards: CardId[]) => [...cards].sort((a, b) => rank(a) - rank(b))[0]!;
const highestOf = (cards: CardId[]) => [...cards].sort((a, b) => rank(b) - rank(a))[0]!;

function chooseCard(view: HeartsView, legal: CardId[], ctx: BotContext): CardId {
  if (legal.length === 1) return legal[0]!;
  const unseen = ctx.profile.memory > 0 ? unseenCards(view, ctx) : null;
  const queenOut = unseen ? unseen.has(QUEEN_SPADES) : true;
  const trick: HeartsTrickPlay[] = view.trick;
  const hasQueen = view.hand.includes(QUEEN_SPADES);
  const mySpades = view.hand.filter((c) => parseCard(c).suit === 'S').length;

  if (trick.length === 0) {
    // Leading: the card least likely to win the trick or to bring the Q♠ down on us.
    const score = (c: CardId) => {
      const card = parseCard(c);
      let s = card.rank;
      if (card.suit === 'S') {
        if (hasQueen && mySpades <= 4) s += 30;
        else if (queenOut && card.rank >= 13) s += 25;
        else if (queenOut && !hasQueen) s -= 4; // a low spade lead helps flush the Q♠ out of someone else's hand
      }
      if (card.suit === 'H') s += 6;
      return s;
    };
    return [...legal].sort((a, b) => score(a) - score(b))[0]!;
  }

  const lead = parseCard(trick[0]!.card).suit;
  const following = legal.every((c) => parseCard(c).suit === lead);
  const last = trick.length === 3;
  const trickPoints = trick.reduce((n, p) => n + heartsCardPoints(p.card), 0);
  if (following) {
    const winning = Math.max(...trick.filter((p) => parseCard(p.card).suit === lead).map((p) => rank(p.card)));
    const under = legal.filter((c) => rank(c) < winning);
    if (under.length > 0) return highestOf(under);
    // Cannot duck: we will win unless someone behind us plays higher.
    if (lead === 'S' && queenOut && !last && !hasQueen) return lowestOf(legal);
    if (last && trickPoints === 0) {
      // Taking a clean trick is fine; shed the highest card, but never a Q♠ onto ourselves unless forced.
      const safe = legal.filter((c) => c !== QUEEN_SPADES);
      return highestOf(safe.length ? safe : legal);
    }
    const safe = legal.filter((c) => c !== QUEEN_SPADES);
    return highestOf(safe.length ? safe : legal);
  }

  // Void in the led suit: get rid of the dangerous cards.
  if (legal.includes(QUEEN_SPADES)) return QUEEN_SPADES;
  const dangerousSpades = legal.filter((c) => parseCard(c).suit === 'S' && rank(c) >= 13);
  if (dangerousSpades.length > 0 && queenOut) return highestOf(dangerousSpades);
  const hearts = legal.filter((c) => parseCard(c).suit === 'H');
  if (hearts.length > 0) return highestOf(hearts);
  return highestOf(legal);
}

export const heartsBot: BotEngine<HeartsView, HeartsLegal, HeartsAction> = {
  decideAction(view, legal, ctx) {
    if (legal.kind === 'pass') {
      const hand = view.hand;
      if ((ctx.difficulty === 'BEGINNER' || ctx.difficulty === 'EASY') && chance(ctx, ctx.profile.mistakeRate)) {
        const pool = [...hand];
        const cards: CardId[] = [];
        while (cards.length < legal.count) cards.push(pool.splice(ctx.rng.int(pool.length), 1)[0]!);
        return { type: 'pass', cards };
      }
      return { type: 'pass', cards: heartsChoosePass(hand) };
    }
    if (legal.kind === 'play') return this.chooseCard!(view, legal, ctx);
    throw new Error('Bot asked to act without legal actions');
  },

  chooseCard(view, legal, ctx) {
    if (legal.kind !== 'play') return this.handleTimeout(view, legal, ctx);
    const cards = legal.cards;
    if (cards.length === 1) return { type: 'play', card: cards[0]! };
    if (chance(ctx, ctx.profile.mistakeRate)) return { type: 'play', card: cards[ctx.rng.int(cards.length)]! };
    const card = chooseCard(view, cards, ctx);
    return { type: 'play', card: cards.includes(card) ? card : cards[0]! };
  },

  evaluateState(view) {
    const me = view.seat;
    if (me === null) return 0;
    const others = view.totals.filter((_, s) => s !== me);
    const avg = others.reduce((a, b) => a + b, 0) / others.length;
    return Math.max(0, Math.min(1, 0.5 + (avg - view.totals[me]!) / 100));
  },

  calculateRisk(view, legal) {
    if (legal.kind === 'play') return Math.min(1, Math.max(0, legal.cards.reduce((n, c) => n + heartsCardPoints(c), 0) / 26));
    return 0;
  },

  handleTimeout(view, legal) {
    if (legal.kind === 'pass') return { type: 'pass', cards: heartsChoosePass(view.hand) };
    if (legal.kind === 'play') {
      const best = [...legal.cards].sort((a, b) => heartsCardPoints(a) - heartsCardPoints(b) || rank(a) - rank(b))[0]!;
      return { type: 'play', card: best };
    }
    throw new Error('No legal action');
  },
};
